import React, { useState, useEffect, useRef } from 'react';
import toast from './../utils/toast';
import api from '../services/api';
import {
  PlusIcon,
  TrashIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import TrademarkDocumentUpload from './TrademarkDocumentUpload';
import ClassAutocomplete from './ClassAutocomplete';
import { useFeeSettings } from '../utils/feeSettings';
import {
  isStartupClientTypeName,
  isSmallEnterpriseStyleClientTypeName
} from '../utils/trademarkClientTypes';

const TrademarkStep2 = ({ application, settings, onComplete, saving, onPreviousStep, canEdit = true }) => {
  const { showFees, loading: feeSettingsLoading } = useFeeSettings();
  
  const [trademarks, setTrademarks] = useState([]);
  const [showAddTrademark, setShowAddTrademark] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [fees, setFees] = useState(null);
  const [loadingFees, setLoadingFees] = useState(false);
  const [editingLanguageIndex, setEditingLanguageIndex] = useState(null);
  const previousApplicationRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  // Force reload application data to get latest documents
  const reloadApplicationData = async () => {
    try {
      const response = await api.get(`/trademark-applications/${application._id}`);
      console.log('Reloaded application data:', response.data.data);
      return response.data.data;
    } catch (error) {
      console.warn('Failed to reload application data:', error);
      return application;
    }
  };

  // Calculate fees based on application data
  const calculateFees = async () => {
    if (!application || !application.country || !application.entityType) {
      setFees(null);
      return;
    }

    try {
      setLoadingFees(true);

      // Convert dropdown value to numeric for backend
      const turnoverValue = application.turnover === 'less_than_2crore' ? 10000000 : 30000000; // 1 CR or 3 CR as representative values

      const response = await api.post('/trademark-applications/calculate-fees', {
        country: application.country,
        entityType: application.entityType,
        turnover: turnoverValue,
        udyogAadhaarAssistance: application.udyogAadhaarAssistance
      });

      setFees(response.data.fees);
    } catch (error) {
      console.error('Failed to calculate fees:', error);
      toast.error('Failed to calculate fees');
    } finally {
      setLoadingFees(false);
    }
  };

  // Get filing type based on application data
  const getFilingType = () => {
    if (!application || !application.country) {
      return null;
    }

    const isIndia = application.country === 'India';

    // If not India, return International
    if (!isIndia) {
      return 'International';
    }

    // Get entity type name from the application data
    const entityTypeName = application.entityType?.name || application.entityType || '';
    const turnover = application.turnover || 0;
    const isLessThan2Crore = turnover === 'less_than_2crore' || (typeof turnover === 'number' && turnover > 0 && turnover < 20000000);

    console.log('Step 2 Filing type calculation:', {
      entityTypeName,
      turnover,
      isLessThan2Crore,
      isIndia
    });

    // A. Individual
    if (entityTypeName === 'Individual') {
      return 'Individual';
    }

    // B. Startup (client types historically "Other" or named "Startup" / "StartUp")
    if (isStartupClientTypeName(entityTypeName) && isLessThan2Crore) {
      return 'Startup';
    }

    // C. MSME-style entities
    if (isSmallEnterpriseStyleClientTypeName(entityTypeName)) {
      if (isLessThan2Crore) {
        return 'Small Enterprise';
      }
      return 'Others';
    }

    // D. All other cases
    return 'Others';
  };

  // Auto-save function
  const autoSave = async (trademarksData) => {
    try {
      setAutoSaving(true);

      console.log('Auto-save: Starting with trademarksData:', trademarksData);
      console.log('Auto-save: Current application:', application);

      // First, try to reload application data to get latest documents
      const latestApplication = await reloadApplicationData();

      // Clean trademarks data for auto-save - exclude className and ensure required fields
      // Also merge with existing documents from application
      const cleanedTrademarks = trademarksData.map((trademark, index) => {
        const { className, ...trademarkWithoutClassName } = trademark;

        // Get existing documents from multiple sources to ensure we don't lose them
        const appTrademark = latestApplication?.trademarks?.[index];
        const currentTrademark = trademarksData[index];
        const existingDocuments = appTrademark?.documents || currentTrademark?.documents || trademark.documents || [];

        console.log(`Auto-save: Trademark ${index} - trademark.documents:`, trademark.documents);
        console.log(`Auto-save: Trademark ${index} - latestApplication.trademarks[${index}].documents:`, appTrademark?.documents);
        console.log(`Auto-save: Trademark ${index} - currentTrademark.documents:`, currentTrademark?.documents);
        console.log(`Auto-save: Trademark ${index} - final documents:`, existingDocuments);
        console.log(`Auto-save: Trademark ${index} - classes:`, trademark.classes);

        return {
          ...trademarkWithoutClassName,
          // Ensure required fields have default values if empty
          trademarkName: trademark.trademarkName || '',
          classDescription: trademark.classDescription || '',
          // Preserve documents and classes
          documents: existingDocuments,
          classes: trademark.classes || []
        };
      });

      // More lenient validation - only filter out completely empty trademarks
      const validTrademarks = cleanedTrademarks.filter(trademark => {
        // Keep trademarks that have at least some basic structure
        const hasBasicStructure = trademark.trademarkType || trademark.trademarkName || 
                                 (Array.isArray(trademark.classes) && trademark.classes.length > 0);

        if (!hasBasicStructure) {
          console.log('Auto-save: Skipping trademark with no basic structure:', trademark);
        }

        return hasBasicStructure;
      });

      // Always save if we have any trademarks or if we're clearing all trademarks
      if (validTrademarks.length > 0 || cleanedTrademarks.length === 0) {
        console.log('Auto-save: Final validTrademarks:', validTrademarks);
        console.log('Auto-save: Sending trademarks with classes:', validTrademarks.map(t => ({
          trademarkName: t.trademarkName,
          documentsCount: t.documents?.length || 0,
          classesCount: t.classes?.length || 0,
          classes: t.classes
        })));

        // Save trademarks to backend
        const response = await api.put(`/trademark-applications/${application._id}`, {
          trademarks: validTrademarks,
          currentStep: 2
        });

        console.log('Auto-save: Response from backend:', response.data);
        setLastSaved(new Date());

        // Sync class-level fields (userAffidavitUrl etc.) back from saved response
        if (response.data?.data?.trademarks) {
          setTrademarks(prev => prev.map((tm, i) => {
            const saved = response.data.data.trademarks[i];
            if (!saved) return tm;
            return {
              ...tm,
              classes: (tm.classes || []).map((cls, ci) => {
                const savedCls = saved.classes?.[ci];
                if (!savedCls) return cls;
                return { ...cls, userAffidavitUrl: savedCls.userAffidavitUrl || cls.userAffidavitUrl || '' };
              })
            };
          }));
        }
      } else {
        console.log('Auto-save: No valid trademarks to save, skipping update');
      }
    } catch (error) {
      console.warn('Auto-save error:', error);

      // Handle specific error types
      if (error.response?.status === 409) {
        console.warn('Version conflict detected, application may have been modified by another user');
        // Optionally reload the application data
        try {
          const reloadedApp = await reloadApplicationData();
          if (reloadedApp) {
            console.log('Successfully reloaded application data after version conflict');
          }
        } catch (reloadError) {
          console.warn('Failed to reload application data after version conflict:', reloadError);
        }
      } else if (error.response?.status === 400) {
        console.warn('Validation error during auto-save:', error.response.data);
      }
    } finally {
      setAutoSaving(false);
    }
  };

  // Manual save function
  const manualSave = async () => {
    try {
      setAutoSaving(true);

      console.log('Manual save: Starting with trademarks:', trademarks);
      console.log('Manual save: Current application:', application);

      // First, try to reload application data to get latest documents
      const latestApplication = await reloadApplicationData();

      // Clean trademarks data for manual save - exclude className and preserve documents
      const cleanedTrademarks = trademarks.map((trademark, index) => {
        const { className, ...trademarkWithoutClassName } = trademark;

        // Get existing documents from multiple sources to ensure we don't lose them
        const appTrademark = latestApplication?.trademarks?.[index];
        const currentTrademark = trademarks[index];
        const existingDocuments = appTrademark?.documents || currentTrademark?.documents || trademark.documents || [];

        console.log(`Manual save: Trademark ${index} - trademark.documents:`, trademark.documents);
        console.log(`Manual save: Trademark ${index} - latestApplication.trademarks[${index}].documents:`, appTrademark?.documents);
        console.log(`Manual save: Trademark ${index} - currentTrademark.documents:`, currentTrademark?.documents);
        console.log(`Manual save: Trademark ${index} - final documents:`, existingDocuments);
        console.log(`Manual save: Trademark ${index} - classes:`, trademark.classes);

        return {
          ...trademarkWithoutClassName,
          // Ensure required fields have default values if empty
          trademarkName: trademark.trademarkName || '',
          classDescription: trademark.classDescription || '',
          // Preserve documents and classes
          documents: existingDocuments,
          classes: trademark.classes || []
        };
      });

      // More lenient validation - only filter out completely empty trademarks
      const validTrademarks = cleanedTrademarks.filter(trademark => {
        // Keep trademarks that have at least some basic structure
        const hasBasicStructure = trademark.trademarkType || trademark.trademarkName || 
                                 (Array.isArray(trademark.classes) && trademark.classes.length > 0);

        if (!hasBasicStructure) {
          console.log('Manual save: Skipping trademark with no basic structure:', trademark);
        }

        return hasBasicStructure;
      });

      // Always save if we have any trademarks or if we're clearing all trademarks
      if (validTrademarks.length > 0 || cleanedTrademarks.length === 0) {
        console.log('Manual save: Final validTrademarks:', validTrademarks);
        console.log('Manual save: Sending trademarks with classes:', validTrademarks.map(t => ({
          trademarkName: t.trademarkName,
          documentsCount: t.documents?.length || 0,
          classesCount: t.classes?.length || 0,
          classes: t.classes
        })));

        const response = await api.put(`/trademark-applications/${application._id}`, {
          trademarks: validTrademarks,
          currentStep: 2
        });

        console.log('Manual save: Response from backend:', response.data);
        setLastSaved(new Date());
        toast.success('Progress saved successfully');
      } else {
        console.log('Manual save: No valid trademarks to save, skipping update');
        toast.warning('Please fill in all required fields before saving');
      }
    } catch (error) {
      console.error('Manual save error:', error);

      // Handle specific error types
      if (error.response?.status === 409) {
        console.warn('Version conflict detected, application may have been modified by another user');
        toast.error('The application was modified by another user. Please refresh and try again.');
        // Optionally reload the application data
        try {
          const reloadedApp = await reloadApplicationData();
          if (reloadedApp) {
            console.log('Successfully reloaded application data after version conflict');
          }
        } catch (reloadError) {
          console.warn('Failed to reload application data after version conflict:', reloadError);
        }
      } else if (error.response?.status === 400) {
        console.warn('Validation error during manual save:', error.response.data);
        toast.error('Validation error: ' + (error.response.data?.message || 'Please check your input'));
      } else {
        toast.error('Failed to save progress');
      }
    } finally {
      setAutoSaving(false);
    }
  };

  // Debounced auto-save
  const debouncedAutoSave = (trademarksData) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Don't auto-save if all trademarks are empty/default
    const hasValidData = trademarksData.some(trademark =>
      trademark.trademarkType &&
      trademark.trademarkName &&
      trademark.class &&
      typeof trademark.class === 'number' &&
      trademark.class >= 1 &&
      trademark.class <= 45 &&
      trademark.classDescription
    );

    if (!hasValidData) {
      console.log('Debounced auto-save: No valid data to save, skipping');
      return;
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave(trademarksData);
    }, 2000); // Save after 2 seconds of inactivity
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only initialize trademarks from application data if:
    // 1. Component is not yet initialized, OR
    // 2. Application data has significantly changed (not just document updates)
    const shouldInitialize = !isInitialized ||
      !previousApplicationRef.current ||
      previousApplicationRef.current.trademarks.length !== application.trademarks.length ||
      JSON.stringify(previousApplicationRef.current.trademarks.map(t => ({
        trademarkType: t.trademarkType,
        trademarkName: t.trademarkName,
        class: t.class
      }))) !== JSON.stringify(application.trademarks.map(t => ({
        trademarkType: t.trademarkType,
        trademarkName: t.trademarkName,
        class: t.class
      })));

    if (shouldInitialize) {
      if (application && application.trademarks && application.trademarks.length > 0) {
        // Ensure existing trademarks have default values for missing fields
        const trademarksWithDefaults = application.trademarks.map(trademark => ({
          ...trademark,
          language: trademark.language || 'English',
          useOfMark: trademark.useOfMark || 'Proposed to be used',
          conditionsAndLimitations: trademark.conditionsAndLimitations || ''
        }));
        setTrademarks(trademarksWithDefaults);
      } else {
        // Create a default trademark if none exist
        const defaultTrademark = {
          trademarkType: '',
          trademarkName: '',
          class: null,
          className: '',
          classDescription: '',
          language: 'English',
          useOfMark: 'Proposed to be used',
          conditionsAndLimitations: '',
          documents: [],
          fees: {
            governmentFees: 0,
            professionalFees: 0,
            totalFees: 0
          }
        };
        setTrademarks([defaultTrademark]);
      }
      setIsInitialized(true);
    } else {
      // For document updates, only update the documents array in existing trademarks
      // to preserve user's form inputs
      if (application && application.trademarks && application.trademarks.length > 0) {
        setTrademarks(prevTrademarks => {
          return prevTrademarks.map((prevTrademark, index) => {
            const appTrademark = application.trademarks[index];
            if (appTrademark) {
              return {
                ...prevTrademark,
                documents: appTrademark.documents || prevTrademark.documents
              };
            }
            return prevTrademark;
          });
        });
      }
    }

    // Store current application for comparison
    previousApplicationRef.current = application;
  }, [application, isInitialized]);

  // Calculate fees when application data changes
  useEffect(() => {
    if (application && application.country && application.entityType) {
      calculateFees();
    }
  }, [application?.country, application?.entityType, application?.turnover, application?.udyogAadhaarAssistance]);

  // Trademark types as per requirements
  const trademarkTypes = [
    'Color Mark (Device)',
    'Logo Mark (Device)',
    'Shape',
    'Sound',
    'Word'
  ];

  // Languages
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'bn', name: 'Bengali' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'mr', name: 'Marathi' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'or', name: 'Odia' },
    { code: 'as', name: 'Assamese' }
  ];

  const renderFeeBreakdown = () => {
    if (!fees) return null;

    const filingType = getFilingType();
    const isIndia = application?.country === 'India';

    // Check if filing type was automatically set due to UDYOG AADHAAR
    const hasUdyogAadhaar = () => {
      if (application?.masterDataId) {
        // This would need to be passed from the parent component or fetched
        // For now, we'll check if the filing type is Small Enterprise and show the indicator
        return filingType === 'Small Enterprise';
      }
      return false;
    };

    const isAutoSetToSmallEnterprise = filingType === 'Small Enterprise' && hasUdyogAadhaar();

    // If fees should be hidden, show a notice instead
    if (!showFees && !feeSettingsLoading) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <InformationCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Fee Information</h4>
              <p className="text-xs text-gray-600 mt-1">
                Fee information is calculated in the background but hidden from view as per system settings.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // If fee settings are still loading, show loading state
    if (feeSettingsLoading) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
            <span className="text-sm text-gray-600">Loading fee settings...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-3">Fee Breakdown</h4>

        {/* Filing Type Display */}
        <div className="mb-3 p-2 bg-blue-100 rounded">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              Filing Type: {filingType}
            </span>
            {isAutoSetToSmallEnterprise && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Auto-set due to UDYOG AADHAAR
              </span>
            )}
          </div>
          {isAutoSetToSmallEnterprise && (
            <p className="text-xs text-green-700 mt-1">
              Filing type automatically set to "Small Enterprise" because UDYOG AADHAAR was found in master data.
            </p>
          )}
        </div>

        <dl className="space-y-2">
          <div className="flex justify-between">
            <dt className="text-sm text-blue-700">Government Fees</dt>
            <dd className="text-sm font-medium text-blue-900">₹{fees.baseFees.governmentFees}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-blue-700">Professional Fees</dt>
            <dd className="text-sm font-medium text-blue-900">₹{fees.baseFees.professionalFees}</dd>
          </div>

          {/* Additional fees for India */}
          {isIndia && fees.baseFees.udyogAadhaarFees > 0 && (
            <div className="flex justify-between">
              <dt className="text-sm text-blue-700">Udyog Aadhaar Fees</dt>
              <dd className="text-sm font-medium text-blue-900">₹{fees.baseFees.udyogAadhaarFees}</dd>
            </div>
          )}

          {/* Udyog Aadhaar Assistance Option */}
          {isIndia && (filingType === 'Small Enterprise' || filingType === 'Startup') && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-800">Udyog Aadhaar Assistance</p>
                  <p className="text-xs text-yellow-700">We can help you obtain Udyog Aadhaar</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-yellow-800">₹{settings?.pricing?.udyogAadhaarAssistance || 1000}</span>
                  <input
                    type="checkbox"
                    checked={application?.udyogAadhaarAssistance || false}
                    disabled
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-blue-200 pt-2">
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-blue-900">Total Base Fees</dt>
              <dd className="text-sm font-bold text-blue-900">₹{fees.totalFees}</dd>
            </div>
            {application?.udyogAadhaarAssistance && (
              <div className="flex justify-between mt-1">
                <dt className="text-sm text-blue-700">+ Udyog Aadhaar Assistance</dt>
                <dd className="text-sm font-medium text-blue-900">₹{settings?.pricing?.udyogAadhaarAssistance || 1000}</dd>
              </div>
            )}
            {(application?.udyogAadhaarAssistance) && (
              <div className="flex justify-between mt-2 pt-2 border-t border-blue-200">
                <dt className="text-sm font-bold text-blue-900">Total Fees</dt>
                <dd className="text-sm font-bold text-blue-900">
                  ₹{fees.totalFees + (application?.udyogAadhaarAssistance ? (settings?.pricing?.udyogAadhaarAssistance || 1000) : 0)}
                </dd>
              </div>
            )}
          </div>
        </dl>
      </div>
    );
  };

  const addTrademark = () => {
    const newTrademark = {
      trademarkType: '',
      trademarkName: '',
      class: null,
      className: '',
      classDescription: '',
      classes: [{
        class: null,
        classDescription: '',
        language: 'English',
        useOfMark: 'Proposed to be used',
        proposedUseDate: '',
        conditionsAndLimitations: '',
        transliteration: '',
        translation: ''
      }],
      language: 'English',
      useOfMark: 'Proposed to be used',
      conditionsAndLimitations: '',
      documents: [],
      fees: {
        governmentFees: 0,
        professionalFees: 0,
        totalFees: 0
      }
    };

    const updatedTrademarks = [...trademarks, newTrademark];
    setTrademarks(updatedTrademarks);
    setEditingIndex(trademarks.length);
    debouncedAutoSave(updatedTrademarks);
    toast.success('New trademark added');
  };

  const updateTrademark = (index, field, value) => {
    const updatedTrademarks = [...trademarks];
    updatedTrademarks[index] = {
      ...updatedTrademarks[index],
      [field]: value
    };
    setTrademarks(updatedTrademarks);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
  };

  const updateTrademarkClass = (index, classNumber, classDescription) => {
    const updatedTrademarks = [...trademarks];
    updatedTrademarks[index] = {
      ...updatedTrademarks[index],
      class: classNumber,
      classDescription: classDescription
    };
    setTrademarks(updatedTrademarks);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
  };

  const enableLanguageEditing = (index) => {
    setEditingLanguageIndex(index);
  };

  const disableLanguageEditing = (index) => {
    setEditingLanguageIndex(null);
  };

  const handleDocumentUrlReceived = (documentData) => {
    const { trademarkIndex, trademarkDocumentType, documentUrl, fileName, fileSize, mimeType } = documentData;

    const updatedTrademarks = [...trademarks];
    const trademark = updatedTrademarks[trademarkIndex];

    // Initialize documents array if it doesn't exist
    if (!trademark.documents) {
      trademark.documents = [];
    }

    // Check if document type already exists
    const existingDocIndex = trademark.documents.findIndex(doc => doc.type === trademarkDocumentType);

    if (existingDocIndex !== -1) {
      // Update existing document
      trademark.documents[existingDocIndex] = {
        ...trademark.documents[existingDocIndex],
        documentUrl,
        fileName,
        fileSize,
        mimeType,
        uploaded: true
      };
    } else {
      // Add new document
      trademark.documents.push({
        type: trademarkDocumentType,
        documentUrl,
        fileName,
        fileSize,
        mimeType,
        uploaded: true,
        required: true
      });
    }

    setTrademarks(updatedTrademarks);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
  };

  const removeTrademark = (index) => {
    const updatedTrademarks = trademarks.filter((_, i) => i !== index);
    setTrademarks(updatedTrademarks);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
  };

  const copyTrademark = (index) => {
    const trademarkToCopy = trademarks[index];
    const newTrademark = {
      ...trademarkToCopy,
      // Clear only the specific trademark content, but keep the structure and settings
      trademarkName: `${trademarkToCopy.trademarkName || 'Trademark'} (Copy)`,
      class: trademarkToCopy.class || null,
      className: trademarkToCopy.className || '',
      classDescription: trademarkToCopy.classDescription || '',
      // Keep documents but create new references
      documents: trademarkToCopy.documents ? [...trademarkToCopy.documents] : []
    };
    const updatedTrademarks = [...trademarks, newTrademark];
    setTrademarks(updatedTrademarks);
    setEditingIndex(trademarks.length);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
    toast.success('Trademark copied successfully');
  };

  // Copy trademark without class information
  const copyTrademarkWithoutClass = (index) => {
    if (!trademarks || trademarks.length === 0) {
      toast.error('No trademarks available to copy');
      return;
    }
    
    const trademarkToCopy = trademarks[index];
    if (!trademarkToCopy) {
      toast.error('Trademark not found');
      return;
    }
    
    const newTrademark = {
      ...trademarkToCopy,
      // Clear class-related fields
      trademarkName: `${trademarkToCopy.trademarkName || 'Trademark'} (Copy)`,
      class: null,
      className: '',
      classDescription: '',
      classes: [], // Clear all classes
      // Keep documents but create new references
      documents: trademarkToCopy.documents ? [...trademarkToCopy.documents] : []
    };
    const updatedTrademarks = [...trademarks, newTrademark];
    setTrademarks(updatedTrademarks);
    setEditingIndex(trademarks.length);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
    toast.success('Trademark copied (without class) successfully');
  };

  // Copy trademark with all data including class
  const copyTrademarkWithAllData = (index) => {
    if (!trademarks || trademarks.length === 0) {
      toast.error('No trademarks available to copy');
      return;
    }
    
    const trademarkToCopy = trademarks[index];
    if (!trademarkToCopy) {
      toast.error('Trademark not found');
      return;
    }
    
    const newTrademark = {
      ...trademarkToCopy,
      // Keep all data including class information
      trademarkName: `${trademarkToCopy.trademarkName || 'Trademark'} (Copy)`,
      // Keep all class-related fields
      class: trademarkToCopy.class,
      className: trademarkToCopy.className,
      classDescription: trademarkToCopy.classDescription,
      classes: trademarkToCopy.classes ? trademarkToCopy.classes.map(cls => ({
        ...cls,
        // Ensure each class has all the required fields
        language: cls.language || 'English',
        useOfMark: cls.useOfMark || 'Proposed to be used',
        proposedUseDate: cls.proposedUseDate || '',
        conditionsAndLimitations: cls.conditionsAndLimitations || '',
        transliteration: cls.transliteration || '',
        translation: cls.translation || ''
      })) : [],
      // Keep documents but create new references
      documents: trademarkToCopy.documents ? [...trademarkToCopy.documents] : []
    };
    const updatedTrademarks = [...trademarks, newTrademark];
    setTrademarks(updatedTrademarks);
    setEditingIndex(trademarks.length);
    debouncedAutoSave(updatedTrademarks); // Auto-save on change
    toast.success('Trademark copied with all data successfully');
  };

  // Modified: Add class/description to the current trademark's classes array
  const addTrademarkFromClass = (classData) => {
    const currentIndex = editingIndex !== null ? editingIndex : 0;
    if (currentIndex >= 0 && currentIndex < trademarks.length) {
      const updatedTrademarks = [...trademarks];
      const currentTrademark = { ...updatedTrademarks[currentIndex] };

      // Ensure classes array exists
      if (!Array.isArray(currentTrademark.classes)) {
        currentTrademark.classes = [];
        // If the trademark already has a class, add it to the array
        if (currentTrademark.class && currentTrademark.classDescription) {
          currentTrademark.classes.push({
            class: currentTrademark.class,
            classDescription: currentTrademark.classDescription,
            language: currentTrademark.language || 'English',
            useOfMark: currentTrademark.useOfMark || 'Proposed to be used',
            proposedUseDate: currentTrademark.proposedUseDate || '',
            conditionsAndLimitations: currentTrademark.conditionsAndLimitations || '',
            transliteration: currentTrademark.transliteration || '',
            translation: currentTrademark.translation || ''
          });
        }
      }

      // Check if class number already exists
      const existingClassIndex = currentTrademark.classes.findIndex(
        cls => cls.class === parseInt(classData.number)
      );

      if (existingClassIndex !== -1) {
        // Class already exists, merge descriptions
        const existingClass = currentTrademark.classes[existingClassIndex];
        const newDescription = classData.description;

        // Merge descriptions (avoid duplicates)
        const existingDesc = existingClass.classDescription || '';
        const mergedDescription = existingDesc.includes(newDescription)
          ? existingDesc
          : existingDesc + (existingDesc ? '; ' : '') + newDescription;

        currentTrademark.classes[existingClassIndex] = {
          ...existingClass,
          classDescription: mergedDescription
        };

        updatedTrademarks[currentIndex] = currentTrademark;
        setTrademarks(updatedTrademarks);
        debouncedAutoSave(updatedTrademarks);
        toast.success(`Updated Class ${classData.number} - merged descriptions`);
      } else {
        // Class doesn't exist, add as new class
        currentTrademark.classes.push({
          class: parseInt(classData.number),
          classDescription: classData.description,
          language: currentTrademark.language || 'English',
          useOfMark: currentTrademark.useOfMark || 'Proposed to be used',
          proposedUseDate: currentTrademark.proposedUseDate || '',
          conditionsAndLimitations: currentTrademark.conditionsAndLimitations || '',
          transliteration: currentTrademark.transliteration || '',
          translation: currentTrademark.translation || ''
        });

        // Keep the main class fields unchanged - they represent the primary class
        // Only update if there was no existing class
        if (!currentTrademark.class) {
          currentTrademark.class = parseInt(classData.number);
          currentTrademark.classDescription = classData.description;
        }

        updatedTrademarks[currentIndex] = currentTrademark;
        setTrademarks(updatedTrademarks);
        debouncedAutoSave(updatedTrademarks);
        toast.success(`Added Class ${classData.number} to current trademark`);
      }
    } else {
      // If no trademark exists, create a new one
      const newTrademark = {
        trademarkType: '',
        trademarkName: '',
        class: parseInt(classData.number),
        classDescription: classData.description,
        classes: [{
          class: parseInt(classData.number),
          classDescription: classData.description,
          language: 'English',
          useOfMark: 'Proposed to be used',
          proposedUseDate: '',
          conditionsAndLimitations: '',
          transliteration: '',
          translation: ''
        }],
        language: 'English',
        useOfMark: 'Proposed to be used',
        conditionsAndLimitations: '',
        documents: [],
        fees: {
          governmentFees: 0,
          professionalFees: 0,
          totalFees: 0
        }
      };

      setTrademarks([...trademarks, newTrademark]);
      setEditingIndex(trademarks.length);
      debouncedAutoSave([...trademarks, newTrademark]);
      toast.success(`Created new trademark with Class ${classData.number}`);
    }
  };

  // Helper function to remove a class from the classes array
  const removeClassFromTrademark = (trademarkIndex, classIndex) => {
    const updatedTrademarks = [...trademarks];
    const currentTrademark = { ...updatedTrademarks[trademarkIndex] };

    if (Array.isArray(currentTrademark.classes) && currentTrademark.classes.length > classIndex) {
      const removedClass = currentTrademark.classes[classIndex];
      currentTrademark.classes.splice(classIndex, 1);

      // If we removed the primary class (index 0), update the main class fields
      if (classIndex === 0 && currentTrademark.classes.length > 0) {
        const newPrimaryClass = currentTrademark.classes[0];
        currentTrademark.class = newPrimaryClass.class;
        currentTrademark.classDescription = newPrimaryClass.classDescription;
      }

      updatedTrademarks[trademarkIndex] = currentTrademark;
      setTrademarks(updatedTrademarks);
      debouncedAutoSave(updatedTrademarks);
      toast.success(`Removed Class ${removedClass.class} - ${removedClass.classDescription}`);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const warnings = [];

    trademarks.forEach((trademark, index) => {
      // Validate trademark type
      if (!trademark.trademarkType) {
        newErrors[`trademark_${index}_type`] = 'Trademark type is required';
      }

      // Validate trademark name
      if (!trademark.trademarkName || trademark.trademarkName.trim() === '') {
        newErrors[`trademark_${index}_name`] = 'Trademark name is required';
      }

      // Validate that at least one class exists
      if (!Array.isArray(trademark.classes) || trademark.classes.length === 0) {
        newErrors[`trademark_${index}_class`] = 'At least one class is required';
      } else {
        // Validate each class
        trademark.classes.forEach((classItem, classIndex) => {
          // Class number validation - CRITICAL
          if (!classItem.class || classItem.class === null || classItem.class === '') {
            newErrors[`trademark_${index}_class_${classIndex}`] = 'Class number is required';
          } else if (classItem.class < 1 || classItem.class > 45) {
            newErrors[`trademark_${index}_class_${classIndex}`] = 'Class number must be between 1 and 45';
          }
          
          // Class description validation - CRITICAL
          if (!classItem.classDescription || classItem.classDescription.trim() === '') {
            newErrors[`trademark_${index}_class_description_${classIndex}`] = 'Class description is required';
          }
          
          // Language validation - use default if not set (WARNING)
          const language = classItem.language || 'English';
          if (!language || language.trim() === '') {
            warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Language will default to English`);
          }
          
          // Use of mark validation - use default if not set (WARNING)
          const useOfMark = classItem.useOfMark || 'Proposed to be used';
          if (!useOfMark || useOfMark.trim() === '') {
            warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Use of mark will default to 'Proposed to be used'`);
          }
          
          // Date validation - only required if "In use" is selected (WARNING)
          if (useOfMark === 'In use' && (!classItem.proposedUseDate || classItem.proposedUseDate.trim() === '')) {
            warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Date is recommended when mark is in use`);
          }
          
          // Language-specific validation - WARNING for non-English languages
          if (language && language !== 'English') {
            if (!classItem.transliteration || classItem.transliteration.trim() === '') {
              warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Transliteration is recommended for non-English language`);
            }
            if (!classItem.translation || classItem.translation.trim() === '') {
              warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Translation is recommended for non-English language`);
            }
          }
        });
      }

      // Validate documents for specific trademark types - WARNING
      const requiresDocument = ['Color Mark (Device)', 'Logo Mark (Device)', 'Shape', 'Sound'].includes(trademark.trademarkType);
      if (requiresDocument && (!trademark.documents || trademark.documents.length === 0)) {
        warnings.push(`Trademark ${index + 1}: Documents are recommended for this trademark type`);
      }
    });

    setErrors(newErrors);
    
    // Log validation errors and warnings for debugging
    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors:', newErrors);
    }
    if (warnings.length > 0) {
      console.log('Validation warnings:', warnings);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get warnings by running validation with warnings
    const warnings = [];
    trademarks.forEach((trademark, index) => {
      if (Array.isArray(trademark.classes) && trademark.classes.length > 0) {
        trademark.classes.forEach((classItem, classIndex) => {
          const language = classItem.language || 'English';
          const useOfMark = classItem.useOfMark || 'Proposed to be used';
          
          if (useOfMark === 'In use' && (!classItem.proposedUseDate || classItem.proposedUseDate.trim() === '')) {
            warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Date is recommended when mark is in use`);
          }
          
          if (language && language !== 'English') {
            if (!classItem.transliteration || classItem.transliteration.trim() === '') {
              warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Transliteration is recommended for non-English language`);
            }
            if (!classItem.translation || classItem.translation.trim() === '') {
              warnings.push(`Trademark ${index + 1}, Class ${classIndex + 1}: Translation is recommended for non-English language`);
            }
          }
        });
      }
      
      const requiresDocument = ['Color Mark (Device)', 'Logo Mark (Device)', 'Shape', 'Sound'].includes(trademark.trademarkType);
      if (requiresDocument && (!trademark.documents || trademark.documents.length === 0)) {
        warnings.push(`Trademark ${index + 1}: Documents are recommended for this trademark type`);
      }
    });

    if (!validateForm()) {
      // Display detailed error messages
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        const errorText = errorMessages.slice(0, 3).join(', '); // Show first 3 errors
        const remainingCount = errorMessages.length - 3;
        const fullErrorText = remainingCount > 0 
          ? `${errorText} and ${remainingCount} more error(s)`
          : errorText;
        toast.error(`Please fix the errors before proceeding: ${fullErrorText}`);
      } else {
        toast.error('Please fix the errors before proceeding');
      }
      return;
    }

    // Show warnings if any, but allow submission
    if (warnings.length > 0) {
      const warningText = warnings.slice(0, 2).join(', '); // Show first 2 warnings
      const remainingWarnings = warnings.length - 2;
      const fullWarningText = remainingWarnings > 0 
        ? `${warningText} and ${remainingWarnings} more warning(s)`
        : warningText;
      toast.warning(`Proceeding with warnings: ${fullWarningText}`);
    }

    console.log('Form submit: Starting with trademarks:', trademarks);

    // Clean trademarks data for submission - exclude className and include document URLs
    const cleanedTrademarks = trademarks.map((trademark, index) => {
      const { className, ...trademarkWithoutClassName } = trademark;

      // Include the documents array with URLs that were uploaded locally
      const documentsWithUrls = trademark.documents || [];

      console.log(`Form submit: Trademark ${index} - documents with URLs:`, documentsWithUrls);

      return {
        ...trademarkWithoutClassName,
        documents: documentsWithUrls
      };
    });

    console.log('Form submit: Final cleanedTrademarks:', cleanedTrademarks);
    console.log('Form submit: Sending trademarks with documents:', cleanedTrademarks.map(t => ({
      trademarkName: t.trademarkName,
      documentsCount: t.documents?.length || 0,
      documents: t.documents
    })));

    const step2Data = { trademarks: cleanedTrademarks };
    console.log('Calling onComplete with:', step2Data);
    onComplete(step2Data);
  };

  const renderTrademarkForm = (trademark, index) => {
    const isEditing = editingIndex === index;
    const requiresDocument = ['Color Mark (Device)', 'Logo Mark (Device)', 'Shape', 'Sound'].includes(trademark.trademarkType);

    return (
      <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Trademark {index + 1}
          </h3>
          {canEdit && (
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => copyTrademark(index)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Copy
              </button>
              {trademarks.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTrademark(index)}
                  className="px-3 py-1 text-sm border border-red-300 rounded-md text-red-700 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Trademark Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trademark Type *
            </label>
            <select
              value={trademark.trademarkType}
              onChange={(e) => updateTrademark(index, 'trademarkType', e.target.value)}
              disabled={!canEdit}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors[`trademark_${index}_type`] ? 'border-red-300' : 'border-gray-300'
              } ${!canEdit ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            >
              <option value="">Select type</option>
              {trademarkTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors[`trademark_${index}_type`] && (
              <p className="mt-1 text-sm text-red-600">{errors[`trademark_${index}_type`]}</p>
            )}
          </div>

          {/* Trademark Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trademark Name *
            </label>
            <input
              type="text"
              value={trademark.trademarkName}
              onChange={(e) => updateTrademark(index, 'trademarkName', e.target.value)}
              disabled={!canEdit}
              placeholder="Enter trademark name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors[`trademark_${index}_name`] ? 'border-red-300' : 'border-gray-300'
              } ${!canEdit ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            />
            {errors[`trademark_${index}_name`] && (
              <p className="mt-1 text-sm text-red-600">{errors[`trademark_${index}_name`]}</p>
            )}
          </div>

          {/* Document Upload Section */}
          {requiresDocument && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Required Documents</h4>
              <TrademarkDocumentUpload
                trademarkIndex={index}
                trademarkType={trademark.trademarkType}
                documents={trademark.documents}
                onDocumentUrlReceived={handleDocumentUrlReceived}
              />
            </div>
            
            
          )}
          {requiresDocument && (
            <div className="mt-6 pt-6 border-t border-gray-200">
            </div>
            
            
          )}

          

          {/* Class Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Class *
            </label>
            <ClassAutocomplete
              classNumber={trademark.class}
              classDescription={trademark.classDescription}
              onClassChange={(classNumber, classDescription) => updateTrademarkClass(index, classNumber, classDescription)}
              onAddClass={addTrademarkFromClass}
              placeholder="Search for trademark class..."
              error={!!errors[`trademark_${index}_class`]}
            />
            {errors[`trademark_${index}_class`] && (
              <p className="mt-1 text-sm text-red-600">{errors[`trademark_${index}_class`]}</p>
            )}
          </div>

          {/* Manually Add Class Button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              &nbsp;
            </label>
            <button
              type="button"
              onClick={() => {
                const updatedTrademarks = [...trademarks];
                const currentTrademark = { ...updatedTrademarks[index] };

                // Ensure classes array exists
                if (!Array.isArray(currentTrademark.classes)) {
                  currentTrademark.classes = [];
                  // If the trademark already has a class, add it to the array
                  if (currentTrademark.class && currentTrademark.classDescription) {
                    currentTrademark.classes.push({
                      class: currentTrademark.class,
                      classDescription: currentTrademark.classDescription,
                      language: currentTrademark.language || 'English',
                      useOfMark: currentTrademark.useOfMark || 'Proposed to be used',
                      proposedUseDate: currentTrademark.proposedUseDate || '',
                      conditionsAndLimitations: currentTrademark.conditionsAndLimitations || '',
                      transliteration: currentTrademark.transliteration || '',
                      translation: currentTrademark.translation || ''
                    });
                  }
                }

                // Add a blank class
                currentTrademark.classes.push({
                  class: null,
                  classDescription: '',
                  language: currentTrademark.language || 'English',
                  useOfMark: currentTrademark.useOfMark || 'Proposed to be used',
                  proposedUseDate: '',
                  conditionsAndLimitations: '',
                  transliteration: '',
                  translation: ''
                });

                updatedTrademarks[index] = currentTrademark;
                setTrademarks(updatedTrademarks);
                debouncedAutoSave(updatedTrademarks);
                toast.success('Added blank class - you can now edit it');
              }}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Manually Add Class
            </button>
          </div>

          {/* All Classes Display */}
          {Array.isArray(trademark.classes) && trademark.classes.length > 0 && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                All Classes for this Trademark
              </label>
              <div className="space-y-4">
                {trademark.classes.map((classItem, classIndex) => (
                  <div key={classIndex} className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-white">{classItem.class || '?'}</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-blue-900">
                            Class {classItem.class || '?'}
                          </div>
                        </div>
                      </div>
                      {classIndex > 0 && (
                        <button
                          type="button"
                          onClick={() => removeClassFromTrademark(index, classIndex)}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                          title="Remove this class"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Class Number */}
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">
                          Class Number
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="45"
                          value={classItem.class || ''}
                          onChange={(e) => {
                            const updatedTrademarks = [...trademarks];
                            const currentTrademark = { ...updatedTrademarks[index] };
                            currentTrademark.classes[classIndex] = {
                              ...currentTrademark.classes[classIndex],
                              class: e.target.value ? parseInt(e.target.value) : null
                            };
                            updatedTrademarks[index] = currentTrademark;
                            setTrademarks(updatedTrademarks);
                            debouncedAutoSave(updatedTrademarks);
                          }}
                          className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                            errors[`trademark_${index}_class_${classIndex}`] ? 'border-red-300' : 'border-blue-300'
                          }`}
                          placeholder="1-45"
                        />
                        {errors[`trademark_${index}_class_${classIndex}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`trademark_${index}_class_${classIndex}`]}</p>
                        )}
                      </div>

                      {/* Class Description */}
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">
                          Class Description
                        </label>
                        <textarea
                          value={classItem.classDescription || ''}
                          onChange={(e) => {
                            const updatedTrademarks = [...trademarks];
                            const currentTrademark = { ...updatedTrademarks[index] };
                            currentTrademark.classes[classIndex] = {
                              ...currentTrademark.classes[classIndex],
                              classDescription: e.target.value
                            };
                            updatedTrademarks[index] = currentTrademark;
                            setTrademarks(updatedTrademarks);
                            debouncedAutoSave(updatedTrademarks);
                          }}
                          rows={2}
                          className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                            errors[`trademark_${index}_class_description_${classIndex}`] ? 'border-red-300' : 'border-blue-300'
                          }`}
                          placeholder="Enter class description"
                        />
                        {errors[`trademark_${index}_class_description_${classIndex}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`trademark_${index}_class_description_${classIndex}`]}</p>
                        )}
                      </div>

                      {/* Language */}
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">
                          Language
                        </label>
                        <select
                          value={classItem.language || 'English'}
                          onChange={(e) => {
                            const updatedTrademarks = [...trademarks];
                            const currentTrademark = { ...updatedTrademarks[index] };
                            currentTrademark.classes[classIndex] = {
                              ...currentTrademark.classes[classIndex],
                              language: e.target.value
                            };
                            updatedTrademarks[index] = currentTrademark;
                            setTrademarks(updatedTrademarks);
                            debouncedAutoSave(updatedTrademarks);
                          }}
                          className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.name}>
                              {lang.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Use of Mark */}
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">
                          Use of Mark
                        </label>
                        <select
                          value={classItem.useOfMark || 'Proposed to be used'}
                          onChange={(e) => {
                            const updatedTrademarks = [...trademarks];
                            const currentTrademark = { ...updatedTrademarks[index] };
                            currentTrademark.classes[classIndex] = {
                              ...currentTrademark.classes[classIndex],
                              useOfMark: e.target.value
                            };
                            updatedTrademarks[index] = currentTrademark;
                            setTrademarks(updatedTrademarks);
                            debouncedAutoSave(updatedTrademarks);
                          }}
                          className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="Proposed to be used">Proposed to be used</option>
                          <option value="In use">In use</option>
                        </select>
                      </div>

                      {/* Proposed Use Date */}
                      {classItem.useOfMark === 'In use' && (
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-1">
                            Date since when you are using
                          </label>
                          <input
                            type="date"
                            value={classItem.proposedUseDate || ''}
                            onChange={(e) => {
                              const updatedTrademarks = [...trademarks];
                              const currentTrademark = { ...updatedTrademarks[index] };
                              currentTrademark.classes[classIndex] = {
                                ...currentTrademark.classes[classIndex],
                                proposedUseDate: e.target.value
                              };
                              updatedTrademarks[index] = currentTrademark;
                              setTrademarks(updatedTrademarks);
                              debouncedAutoSave(updatedTrademarks);
                            }}
                            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}

                      {/* User Affidavit Upload (required when In use) */}
                      {classItem.useOfMark === 'In use' && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-blue-700 mb-1">
                            User Affidavit <span className="text-red-500">*</span>
                            <span className="ml-1 font-normal text-gray-500">(required for filing when mark is In use)</span>
                          </label>
                          {classItem.userAffidavitUrl ? (
                            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                              <span className="text-green-700 truncate">Affidavit uploaded</span>
                              <a
                                href={classItem.userAffidavitUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto text-blue-600 hover:underline whitespace-nowrap"
                              >
                                View
                              </a>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedTrademarks = [...trademarks];
                                  const currentTrademark = { ...updatedTrademarks[index] };
                                  currentTrademark.classes[classIndex] = {
                                    ...currentTrademark.classes[classIndex],
                                    userAffidavitUrl: ''
                                  };
                                  updatedTrademarks[index] = currentTrademark;
                                  setTrademarks(updatedTrademarks);
                                  debouncedAutoSave(updatedTrademarks);
                                }}
                                className="text-red-500 hover:text-red-700"
                                title="Remove"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <label className="flex items-center justify-center gap-2 p-2 border border-dashed border-blue-300 rounded cursor-pointer bg-blue-50 hover:bg-blue-100 text-xs text-blue-600">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  try {
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    const response = await api.post('/upload/public', formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    if (response.data.success) {
                                      const updatedTrademarks = [...trademarks];
                                      const currentTrademark = { ...updatedTrademarks[index] };
                                      currentTrademark.classes[classIndex] = {
                                        ...currentTrademark.classes[classIndex],
                                        userAffidavitUrl: response.data.url
                                      };
                                      updatedTrademarks[index] = currentTrademark;
                                      setTrademarks(updatedTrademarks);
                                      debouncedAutoSave(updatedTrademarks);
                                    }
                                  } catch (err) {
                                    console.error('Affidavit upload failed', err);
                                  }
                                }}
                              />
                              <DocumentIcon className="h-4 w-4" />
                              Upload User Affidavit (PDF / JPG / PNG)
                            </label>
                          )}
                        </div>
                      )}

                      {/* Transliteration */}
                      {classItem.language && classItem.language !== 'English' && (
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-1">
                            Transliteration
                          </label>
                          <input
                            type="text"
                            value={classItem.transliteration || ''}
                            onChange={(e) => {
                              const updatedTrademarks = [...trademarks];
                              const currentTrademark = { ...updatedTrademarks[index] };
                              currentTrademark.classes[classIndex] = {
                                ...currentTrademark.classes[classIndex],
                                transliteration: e.target.value
                              };
                              updatedTrademarks[index] = currentTrademark;
                              setTrademarks(updatedTrademarks);
                              debouncedAutoSave(updatedTrademarks);
                            }}
                            placeholder="Enter transliteration"
                            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}

                      {/* Translation */}
                      {classItem.language && classItem.language !== 'English' && (
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-1">
                            Translation
                          </label>
                          <input
                            type="text"
                            value={classItem.translation || ''}
                            onChange={(e) => {
                              const updatedTrademarks = [...trademarks];
                              const currentTrademark = { ...updatedTrademarks[index] };
                              currentTrademark.classes[classIndex] = {
                                ...currentTrademark.classes[classIndex],
                                translation: e.target.value
                              };
                              updatedTrademarks[index] = currentTrademark;
                              setTrademarks(updatedTrademarks);
                              debouncedAutoSave(updatedTrademarks);
                            }}
                            placeholder="Enter translation"
                            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Conditions and Limitations - Full Width */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-blue-700 mb-1">
                        Conditions and Limitations
                      </label>
                      <textarea
                        value={classItem.conditionsAndLimitations || ''}
                        onChange={(e) => {
                          const updatedTrademarks = [...trademarks];
                          const currentTrademark = { ...updatedTrademarks[index] };
                          currentTrademark.classes[classIndex] = {
                            ...currentTrademark.classes[classIndex],
                            conditionsAndLimitations: e.target.value
                          };
                          updatedTrademarks[index] = currentTrademark;
                          setTrademarks(updatedTrademarks);
                          debouncedAutoSave(updatedTrademarks);
                        }}
                        placeholder="Enter any conditions or limitations for this class"
                        rows={2}
                        className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Each class can have its own language, use of mark, date, and conditions. The first class is the primary class. Additional classes can be removed individually.
              </p>
            </div>
          )}

        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Step 2: Trademark Details</h2>

          {/* Save Status and Manual Save Button */}
          <div className="flex items-center space-x-3">
            {autoSaving && (
              <div className="flex items-center text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm">Auto-saving...</span>
              </div>
            )}

            {lastSaved && !autoSaving && (
              <div className="flex items-center text-green-600">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              </div>
            )}

            <button
              type="button"
              onClick={manualSave}
              disabled={autoSaving || saving}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {autoSaving ? 'Saving...' : 'Save Progress'}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Trademarks List */}
          {trademarks.map((trademark, index) => renderTrademarkForm(trademark, index))}

          {/* Add Trademark Button */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={addTrademark}
              disabled={!canEdit}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                canEdit 
                  ? 'text-gray-700 bg-white hover:bg-gray-50' 
                  : 'text-gray-400 bg-gray-100 cursor-not-allowed'
              }`}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Another Trademark
            </button>
          </div>

          {/* Copy Options */}
          {trademarks.length > 0 && canEdit && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Copy Options</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => copyTrademarkWithoutClass(0)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="font-medium">Copy First Trademark (except class)</div>
                    <div className="text-xs text-gray-500">Copies all details except class information</div>
                  </button>
                </div>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => copyTrademarkWithAllData(0)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="font-medium">Copy All Data</div>
                    <div className="text-xs text-gray-500">Copies everything including class information</div>
                  </button>
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                💡 Tip: Use "Copy First Trademark (except class)" when you want to apply for the same trademark in different classes
              </div>
            </div>
          )}

          {/* Fee Breakdown */}
          {loadingFees ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ) : (
            renderFeeBreakdown()
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            {onPreviousStep && (
              <button
                type="button"
                onClick={onPreviousStep}
                disabled={saving}
                className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Previous Step
              </button>
            )}
            <div className="flex-1"></div>
            <button
              type="submit"
              disabled={saving || !canEdit}
              className={`px-6 py-3 border border-transparent rounded-md text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 ${
                canEdit 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Continue to Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrademarkStep2; 